import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import '../../styles/ip-assessment-tool.css';
import { Disc } from 'lucide-react';
import Disclaimer from '../../components/disclaimer';

const IPAssetAssessmentTool = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [assessmentData, setAssessmentData] = useState({});
  const [formData, setFormData] = useState({
    companyName: '',
    industry: '',
    companyStage: '',
    rdInvestment: '',
    businessDescription: '',
    coreAssets: '',
    competitiveAdvantage: '',
    developmentCosts: '',
    legalCosts: '',
    replacementTime: '',
    marketFactor: '1.0',
    competitionLevel: '1.0',
    revenue: ''
  });
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [ownershipDocs, setOwnershipDocs] = useState([]);
  const canvasRef = useRef(null);

  const totalSteps = 7;

  const industryMultipliers = useMemo(() => ({
    software: 2.5,
    biotech: 4.0,
    hardware: 1.8,
    medical: 3.2,
    fintech: 2.8,
    energy: 2.2,
    manufacturing: 1.5,
    consumer: 2.0,
    automotive: 2.1,
    aerospace: 3.5,
    other: 2.0
  }), []);

  const colors = useMemo(() => ({
    patents: '#3498db',
    trademarks: '#e74c3c',
    copyrights: '#f39c12',
    tradeSecrets: '#27ae60'
  }), []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const toggleCheckbox = (assetId, category, label) => {
    setSelectedAssets(prev => {
      const exists = prev.find(asset => asset.id === assetId);
      if (exists) {
        return prev.filter(asset => asset.id !== assetId);
      } else {
        return [...prev, { id: assetId, category, label }];
      }
    });
  };

  const toggleOwnershipDoc = (docId, category) => {
    setOwnershipDocs(prev => {
      const exists = prev.find(doc => doc.id === docId);
      if (exists) {
        return prev.filter(doc => doc.id !== docId);
      } else {
        return [...prev, { id: docId, category }];
      }
    });
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.companyName || !formData.industry) {
          alert('Please fill in company name and industry');
          return false;
        }
        break;
      case 2:
        if (selectedAssets.length === 0) {
          alert('Please select at least one IP asset type');
          return false;
        }
        break;
      case 5:
        if (!formData.developmentCosts || formData.developmentCosts <= 0) {
          alert('Please enter development costs for valuation');
          return false;
        }
        break;
    }
    return true;
  };

  const saveCurrentStepData = () => {
    switch (currentStep) {
      case 1:
        setAssessmentData(prev => ({
          ...prev,
          companyName: formData.companyName,
          industry: formData.industry,
          companyStage: formData.companyStage,
          rdInvestment: parseFloat(formData.rdInvestment) || 0,
          businessDescription: formData.businessDescription
        }));
        break;
      case 2:
        setAssessmentData(prev => ({
          ...prev,
          selectedAssets: selectedAssets
        }));
        break;
      case 3:
        setAssessmentData(prev => ({
          ...prev,
          coreAssets: formData.coreAssets,
          competitiveAdvantage: formData.competitiveAdvantage
        }));
        break;
      case 4:
        setAssessmentData(prev => ({
          ...prev,
          ownershipDocs: ownershipDocs
        }));
        break;
      case 5:
        setAssessmentData(prev => ({
          ...prev,
          developmentCosts: parseFloat(formData.developmentCosts) || 0,
          legalCosts: parseFloat(formData.legalCosts) || 0,
          replacementTime: parseFloat(formData.replacementTime) || 12,
          marketFactor: parseFloat(formData.marketFactor) || 1.0,
          competitionLevel: parseFloat(formData.competitionLevel) || 1.0,
          revenue: parseFloat(formData.revenue) || 0
        }));
        break;
    }
  };

  const nextStep = () => {
    if (validateCurrentStep()) {
      saveCurrentStepData();
      if (currentStep < totalSteps) {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const calculateValuation = () => {
    if (!validateCurrentStep()) return;

    saveCurrentStepData();

    const totalCosts = parseFloat(formData.developmentCosts) + parseFloat(formData.legalCosts || 0);
    const replacementCostFactor = Math.max(1.0, parseFloat(formData.replacementTime || 12) / 12);
    const marketAdjustment = parseFloat(formData.marketFactor) * parseFloat(formData.competitionLevel);

    const costBasedValue = totalCosts * replacementCostFactor * marketAdjustment;

    let marketBasedValue = 0;
    if (formData.industry) {
      marketBasedValue = totalCosts * (industryMultipliers[formData.industry] || 2.0);
    }

    let incomeBasedValue = 0;
    if (parseFloat(formData.revenue) > 0) {
      const ipContributionRate = 0.15;
      const discountRate = 0.20;
      const growthPeriod = 5;
      incomeBasedValue = (parseFloat(formData.revenue) * ipContributionRate * growthPeriod) / (1 + discountRate);
    }

    let finalValuation;
    if (parseFloat(formData.revenue) > 0) {
      finalValuation = (costBasedValue * 0.6) + (marketBasedValue * 0.2) + (incomeBasedValue * 0.2);
    } else {
      finalValuation = (costBasedValue * 0.7) + (marketBasedValue * 0.3);
    }

    setAssessmentData(prev => ({
      ...prev,
      valuation: {
        costBased: Math.round(costBasedValue),
        marketBased: Math.round(marketBasedValue),
        incomeBased: Math.round(incomeBasedValue),
        final: Math.round(finalValuation),
        methodology: parseFloat(formData.revenue) > 0 ? 'Weighted (Cost 60%, Market 20%, Income 20%)' : 'Weighted (Cost 70%, Market 30%)'
      }
    }));

    nextStep();
  };

  const generatePortfolioVisualization = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!selectedAssets || selectedAssets.length === 0) {
      ctx.fillStyle = '#666';
      ctx.font = '16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('No IP assets selected yet', canvas.width / 2, canvas.height / 2);
      return;
    }

    const categories = {};
    selectedAssets.forEach(asset => {
      categories[asset.category] = (categories[asset.category] || 0) + 1;
    });

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = 100;
    let currentAngle = 0;
    const total = Object.values(categories).reduce((sum, count) => sum + count, 0);

    Object.entries(categories).forEach(([category, count]) => {
      const sliceAngle = (count / total) * 2 * Math.PI;

      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, currentAngle, currentAngle + sliceAngle);
      ctx.closePath();
      ctx.fillStyle = colors[category] || '#95a5a6';
      ctx.fill();

      const labelAngle = currentAngle + sliceAngle / 2;
      const labelX = centerX + Math.cos(labelAngle) * (radius + 30);
      const labelY = centerY + Math.sin(labelAngle) * (radius + 30);

      ctx.fillStyle = '#2c3e50';
      ctx.font = '12px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(`${category.charAt(0).toUpperCase() + category.slice(1)}: ${count}`, labelX, labelY);

      currentAngle += sliceAngle;
    });
  }, [selectedAssets, colors]);

  const generateGapAnalysis = () => {
    let gaps = [];
    let recommendations = [];

    const hasPatents = selectedAssets.some(asset => asset.category === 'patents');
    const hasTrademarks = selectedAssets.some(asset => asset.category === 'trademarks');
    const hasEmployeeAgreements = ownershipDocs.some(doc => doc.id === 'employeeAgreements');
    const hasFounderAgreements = ownershipDocs.some(doc => doc.id === 'founderAgreements');

    if (!hasPatents && ['software', 'biotech', 'hardware', 'medical'].includes(formData.industry)) {
      gaps.push('Patent Protection');
      recommendations.push({
        priority: 'High',
        action: 'Conduct patent landscape analysis and file provisional patents for key innovations',
        timeline: '3-6 months',
        estimatedCost: '$15,000 - $30,000'
      });
    }

    if (!hasTrademarks) {
      gaps.push('Trademark Registration');
      recommendations.push({
        priority: 'Medium',
        action: 'Register core trademarks for company name, product names, and key slogans',
        timeline: '6-12 months',
        estimatedCost: '$2,000 - $5,000'
      });
    }

    if (!hasEmployeeAgreements || !hasFounderAgreements) {
      gaps.push('IP Assignment Documentation');
      recommendations.push({
        priority: 'Critical',
        action: 'Implement comprehensive IP assignment agreements for all team members',
        timeline: '1-2 months',
        estimatedCost: '$3,000 - $8,000'
      });
    }

    return { gaps, recommendations };
  };

  const saveAssessment = () => {
    const assessmentSummary = {
      ...assessmentData,
      completedDate: new Date().toISOString(),
      assessmentId: 'IP_' + Date.now()
    };

    const savedAssessments = JSON.parse(localStorage.getItem('ipAssessments') || '[]');
    savedAssessments.push(assessmentSummary);
    localStorage.setItem('ipAssessments', JSON.stringify(savedAssessments));

    alert('Assessment saved successfully! Assessment ID: ' + assessmentSummary.assessmentId);
  };

  const resetAssessment = () => {
    if (window.confirm('Are you sure you want to start a new assessment? All current data will be lost.')) {
      setAssessmentData({});
      setFormData({
        companyName: '',
        industry: '',
        companyStage: '',
        rdInvestment: '',
        businessDescription: '',
        coreAssets: '',
        competitiveAdvantage: '',
        developmentCosts: '',
        legalCosts: '',
        replacementTime: '',
        marketFactor: '1.0',
        competitionLevel: '1.0',
        revenue: ''
      });
      setSelectedAssets([]);
      setOwnershipDocs([]);
      setCurrentStep(1);
    }
  };

  useEffect(() => {
    if (currentStep === 3) {
      generatePortfolioVisualization();
    }
  }, [currentStep, generatePortfolioVisualization]);

  const renderTooltip = (text) => (
    <span className="ip-assessment-tooltip">
      ℹ️
      <span className="ip-assessment-tooltiptext">{text}</span>
    </span>
  );

  const renderCheckboxGroup = (items, selectedItems, toggleFunction, category) => (
    <div className="ip-assessment-checkbox-group">
      {items.map(item => (
        <div
          key={item.id}
          className={`ip-assessment-checkbox-item ${selectedItems.some(selected => selected.id === item.id) ? 'checked' : ''}`}
          onClick={() => toggleFunction(item.id, category, item.label)}
        >
          <input
            type="checkbox"
            checked={selectedItems.some(selected => selected.id === item.id)}
            onChange={() => { }} // Handled by onClick
          />
          <label>{item.label}</label>
        </div>
      ))}
    </div>
  );

  const renderStepIndicators = () => (
    <div className="ip-assessment-step-indicator">
      {Array.from({ length: totalSteps }, (_, index) => (
        <div
          key={index + 1}
          className={`ip-assessment-step-dot ${index + 1 === currentStep ? 'active' :
            index + 1 < currentStep ? 'completed' : ''
            }`}
        />
      ))}
    </div>
  );

  const seo = {
    metaDesc: 'Comprehensive intellectual property evaluation tool for startups - Inventiv.org'
  };

  useEffect(() => {
    document?.body.classList.add('no-home');
  }, []);

  return (
    <>
      <div className="ip-assessment-tool">
        <div className="ip-assessment-container">
          <div className="ip-assessment-header">
            <h1 className='text-white'>IP Asset Assessment Tool</h1>
            <p className='text-white'>Comprehensive intellectual property evaluation for startups</p>
            <div className="ip-assessment-progress-bar">
              <div
                className="ip-assessment-progress-fill"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              />
            </div>
          </div>

          {renderStepIndicators()}

          <div className="ip-assessment-content">
            {/* Step 1: Business Context */}
            {currentStep === 1 && (
              <div className="ip-assessment-step">
                <h2>Business Context & Industry Information</h2>

                <div className="ip-assessment-form-group">
                  <label htmlFor="companyName">Company Name</label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    placeholder="Enter your company name"
                  />
                </div>

                <div className="ip-assessment-form-group">
                  <label htmlFor="industry">
                    Industry Sector
                    {renderTooltip("Selecting your industry helps us provide sector-specific IP guidance and benchmarks for valuation.")}
                  </label>
                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                  >
                    <option value="">Select your industry</option>
                    <option value="software">Software & Technology</option>
                    <option value="biotech">Biotechnology & Life Sciences</option>
                    <option value="hardware">Hardware & Electronics</option>
                    <option value="medical">Medical Devices</option>
                    <option value="fintech">Financial Technology</option>
                    <option value="energy">Clean Energy & Sustainability</option>
                    <option value="manufacturing">Manufacturing & Industrial</option>
                    <option value="consumer">Consumer Products</option>
                    <option value="automotive">Automotive & Transportation</option>
                    <option value="aerospace">Aerospace & Defense</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="ip-assessment-form-group">
                  <label htmlFor="companyStage">Company Stage</label>
                  <select
                    name="companyStage"
                    value={formData.companyStage}
                    onChange={handleInputChange}
                  >
                    <option value="">Select your current stage</option>
                    <option value="ideation">Ideation/Concept</option>
                    <option value="prototype">Prototype Development</option>
                    <option value="mvp">MVP/Beta Testing</option>
                    <option value="early-revenue">Early Revenue</option>
                    <option value="growth">Growth Stage</option>
                    <option value="established">Established</option>
                  </select>
                </div>

                <div className="ip-assessment-form-group">
                  <label htmlFor="rdInvestment">
                    Total R&D Investment to Date ($)
                    {renderTooltip("Include all development costs, research expenses, prototyping, and technical staff time invested in creating your IP assets.")}
                  </label>
                  <input
                    type="number"
                    name="rdInvestment"
                    value={formData.rdInvestment}
                    onChange={handleInputChange}
                    placeholder="e.g., 150000"
                    min="0"
                  />
                </div>

                <div className="ip-assessment-form-group">
                  <label htmlFor="businessDescription">Brief Business Description</label>
                  <textarea
                    name="businessDescription"
                    value={formData.businessDescription}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder="Describe your business, products/services, and key innovations..."
                  />
                </div>

                <div className="ip-assessment-navigation">
                  <div></div>
                  <button className="ip-assessment-btn" onClick={nextStep}>
                    Continue to IP Discovery
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: IP Asset Discovery */}
            {currentStep === 2 && (
              <div className="ip-assessment-step">
                <h2>IP Asset Discovery</h2>
                <p style={{ marginBottom: '30px', color: '#666', fontSize: '1.1em' }}>
                  Let's identify all your intellectual property assets. Many startups overlook valuable IP they already own.
                </p>

                <div className="ip-assessment-form-group">
                  <label>
                    Patents & Patent Applications
                    {renderTooltip("Patents protect inventions and technical innovations. Include utility patents, design patents, and provisional applications.")}
                  </label>
                  {renderCheckboxGroup([
                    { id: 'utilityPatents', label: 'Utility Patents (Filed/Granted)' },
                    { id: 'designPatents', label: 'Design Patents' },
                    { id: 'provisionalPatents', label: 'Provisional Patent Applications' },
                    { id: 'patentableInventions', label: 'Unprotected Patentable Inventions' }
                  ], selectedAssets, toggleCheckbox, 'patents')}
                </div>

                <div className="ip-assessment-form-group">
                  <label>
                    Trademarks & Brand Assets
                    {renderTooltip("Trademarks protect brand names, logos, slogans, and distinctive business identifiers.")}
                  </label>
                  {renderCheckboxGroup([
                    { id: 'registeredTrademarks', label: 'Registered Trademarks' },
                    { id: 'pendingTrademarks', label: 'Pending Trademark Applications' },
                    { id: 'unregisteredMarks', label: 'Unregistered Trademarks (Common Law)' },
                    { id: 'domainNames', label: 'Valuable Domain Names' }
                  ], selectedAssets, toggleCheckbox, 'trademarks')}
                </div>

                <div className="ip-assessment-form-group">
                  <label>
                    Copyrights & Creative Works
                    {renderTooltip("Copyrights protect original creative works including software code, documentation, marketing materials, and content.")}
                  </label>
                  {renderCheckboxGroup([
                    { id: 'softwareCode', label: 'Software Code & Applications' },
                    { id: 'documentation', label: 'Technical Documentation' },
                    { id: 'marketingContent', label: 'Marketing & Creative Content' },
                    { id: 'trainingMaterials', label: 'Training Materials & Courses' }
                  ], selectedAssets, toggleCheckbox, 'copyrights')}
                </div>

                <div className="ip-assessment-form-group">
                  <label>
                    Trade Secrets & Confidential Information
                    {renderTooltip("Trade secrets include proprietary processes, formulas, algorithms, customer lists, and other confidential business information that provides competitive advantage.")}
                  </label>
                  {renderCheckboxGroup([
                    { id: 'algorithms', label: 'Proprietary Algorithms' },
                    { id: 'processes', label: 'Manufacturing/Business Processes' },
                    { id: 'customerData', label: 'Customer Lists & Data' },
                    { id: 'knowHow', label: 'Technical Know-How' }
                  ], selectedAssets, toggleCheckbox, 'tradeSecrets')}
                </div>

                <div className="ip-assessment-navigation">
                  <button className="ip-assessment-btn ip-assessment-btn-secondary" onClick={prevStep}>
                    Back
                  </button>
                  <button className="ip-assessment-btn" onClick={nextStep}>
                    Continue to Asset Mapping
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Asset Mapping & Visualization */}
            {currentStep === 3 && (
              <div className="ip-assessment-step">
                <h2>Asset Mapping & Portfolio Visualization</h2>
                <p style={{ marginBottom: '30px', color: '#666', fontSize: '1.1em' }}>
                  Let's create a visual map of your IP portfolio and identify key assets.
                </p>

                <div className="ip-assessment-chart-container">
                  <h3>Your IP Portfolio Overview</h3>
                  <canvas ref={canvasRef} width="400" height="300"></canvas>
                </div>

                <div className="ip-assessment-form-group">
                  <label htmlFor="coreAssets">
                    Identify Your Core IP Assets
                    {renderTooltip("List the 3-5 most important IP assets that are central to your business model and competitive advantage.")}
                  </label>
                  <textarea
                    name="coreAssets"
                    value={formData.coreAssets}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder="Describe your most valuable IP assets and how they provide competitive advantage..."
                  />
                </div>

                <div className="ip-assessment-form-group">
                  <label htmlFor="competitiveAdvantage">How does your IP provide competitive advantage?</label>
                  <textarea
                    name="competitiveAdvantage"
                    value={formData.competitiveAdvantage}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Explain how your IP assets differentiate you from competitors..."
                  />
                </div>

                <div className="ip-assessment-navigation">
                  <button className="ip-assessment-btn ip-assessment-btn-secondary" onClick={prevStep}>
                    Back
                  </button>
                  <button className="ip-assessment-btn" onClick={nextStep}>
                    Continue to Ownership Verification
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Ownership Verification */}
            {currentStep === 4 && (
              <div className="ip-assessment-step">
                <h2>Ownership Verification & Documentation</h2>
                <p style={{ marginBottom: '30px', color: '#666', fontSize: '1.1em' }}>
                  Proper ownership documentation is critical for IP value and enforceability.
                </p>

                <div className="ip-assessment-form-group">
                  <label>
                    Employment & Contractor Agreements
                    {renderTooltip("These agreements should include IP assignment clauses to ensure the company owns employee and contractor inventions.")}
                  </label>
                  {renderCheckboxGroup([
                    { id: 'employeeAgreements', label: 'Employee IP Assignment Agreements' },
                    { id: 'contractorAgreements', label: 'Contractor/Consultant Agreements' },
                    { id: 'founderAgreements', label: 'Founder IP Assignment Agreements' }
                  ], ownershipDocs, toggleOwnershipDoc, 'ownership')}
                </div>

                <div className="ip-assessment-form-group">
                  <label>IP Registration Documentation</label>
                  {renderCheckboxGroup([
                    { id: 'patentDocuments', label: 'Patent Applications & Grants' },
                    { id: 'trademarkCertificates', label: 'Trademark Registration Certificates' },
                    { id: 'copyrightRegistrations', label: 'Copyright Registrations' }
                  ], ownershipDocs, toggleOwnershipDoc, 'documentation')}
                </div>

                <div className="ip-assessment-form-group">
                  <label>Trade Secret Protection Measures</label>
                  {renderCheckboxGroup([
                    { id: 'ndaEmployees', label: 'Employee NDAs' },
                    { id: 'ndaThirdParty', label: 'Third-Party NDAs' },
                    { id: 'accessControls', label: 'Access Controls & Security Measures' }
                  ], ownershipDocs, toggleOwnershipDoc, 'tradeSecretProtection')}
                </div>

                <div className="ip-assessment-navigation">
                  <button className="ip-assessment-btn ip-assessment-btn-secondary" onClick={prevStep}>
                    Back
                  </button>
                  <button className="ip-assessment-btn" onClick={nextStep}>
                    Continue to Valuation
                  </button>
                </div>
              </div>
            )}

            {/* Step 5: Multi-Method Valuation */}
            {currentStep === 5 && (
              <div className="ip-assessment-step">
                <h2>IP Asset Valuation</h2>
                <p style={{ marginBottom: '30px', color: '#666', fontSize: '1.1em' }}>
                  We'll use multiple valuation approaches with cost-based methodology as the primary method for early-stage companies.
                </p>

                <div className="ip-assessment-form-group">
                  <label htmlFor="developmentCosts">
                    Development & Creation Costs ($)
                    {renderTooltip("Include all costs to develop the IP: R&D expenses, personnel costs, materials, equipment, and overhead allocations.")}
                  </label>
                  <input
                    type="number"
                    name="developmentCosts"
                    value={formData.developmentCosts}
                    onChange={handleInputChange}
                    placeholder="e.g., 200000"
                    min="0"
                  />
                </div>

                <div className="ip-assessment-form-group">
                  <label htmlFor="legalCosts">Legal & Filing Costs ($)</label>
                  <input
                    type="number"
                    name="legalCosts"
                    value={formData.legalCosts}
                    onChange={handleInputChange}
                    placeholder="e.g., 50000"
                    min="0"
                  />
                </div>

                <div className="ip-assessment-form-group">
                  <label htmlFor="replacementTime">
                    Time to Replace/Recreate (months)
                    {renderTooltip("How long would it take a competitor to develop similar IP assets from scratch?")}
                  </label>
                  <input
                    type="number"
                    name="replacementTime"
                    value={formData.replacementTime}
                    onChange={handleInputChange}
                    placeholder="e.g., 18"
                    min="1"
                  />
                </div>

                <div className="ip-assessment-form-group">
                  <label htmlFor="marketFactor">Market Position Factor</label>
                  <select
                    name="marketFactor"
                    value={formData.marketFactor}
                    onChange={handleInputChange}
                  >
                    <option value="0.8">Emerging market (0.8x)</option>
                    <option value="1.0">Stable market (1.0x)</option>
                    <option value="1.3">High-growth market (1.3x)</option>
                    <option value="1.5">Breakthrough technology (1.5x)</option>
                  </select>
                </div>

                <div className="ip-assessment-form-group">
                  <label htmlFor="competitionLevel">Competition Level</label>
                  <select
                    name="competitionLevel"
                    value={formData.competitionLevel}
                    onChange={handleInputChange}
                  >
                    <option value="1.2">Limited competition (1.2x)</option>
                    <option value="1.0">Moderate competition (1.0x)</option>
                    <option value="0.8">High competition (0.8x)</option>
                    <option value="0.6">Highly commoditized (0.6x)</option>
                  </select>
                </div>

                <div className="ip-assessment-form-group">
                  <label htmlFor="revenue">Current Annual Revenue (if any) ($)</label>
                  <input
                    type="number"
                    name="revenue"
                    value={formData.revenue}
                    onChange={handleInputChange}
                    placeholder="Leave blank if pre-revenue"
                    min="0"
                  />
                </div>

                <div className="ip-assessment-navigation">
                  <button className="ip-assessment-btn ip-assessment-btn-secondary" onClick={prevStep}>
                    Back
                  </button>
                  <button className="ip-assessment-btn" onClick={calculateValuation}>
                    Calculate Valuation
                  </button>
                </div>
              </div>
            )}

            {/* Step 6: Gap Analysis & Recommendations */}
            {currentStep === 6 && (
              <div className="ip-assessment-step">
                <h2>Gap Analysis & Strategic Recommendations</h2>

                <GapAnalysisResults
                  selectedAssets={selectedAssets}
                  ownershipDocs={ownershipDocs}
                  industry={formData.industry}
                  generateGapAnalysis={generateGapAnalysis}
                />

                <div className="ip-assessment-navigation">
                  <button className="ip-assessment-btn ip-assessment-btn-secondary" onClick={prevStep}>
                    Back
                  </button>
                  <button className="ip-assessment-btn" onClick={nextStep}>
                    View Final Dashboard
                  </button>
                </div>
              </div>
            )}

            {/* Step 7: Comprehensive Dashboard */}
            {currentStep === 7 && (
              <div className="ip-assessment-step">
                <h2>IP Portfolio Dashboard & Results</h2>

                <DashboardResults
                  assessmentData={assessmentData}
                  selectedAssets={selectedAssets}
                  ownershipDocs={ownershipDocs}
                  formData={formData}
                />

                <div className="ip-assessment-navigation">
                  <button className="ip-assessment-btn ip-assessment-btn-secondary" onClick={prevStep}>
                    Back
                  </button>
                  <button className="ip-assessment-btn" onClick={saveAssessment}>
                    Save Assessment
                  </button>
                  <button className="ip-assessment-btn" onClick={resetAssessment}>
                    Start New Assessment
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Legal Disclaimer */}
          <div className="ip-assessment-disclaimer">
            <strong>Important Legal Disclaimer:</strong> This is a preliminary assessment tool designed to help founders understand their IP landscape. The valuations provided are estimates for strategic planning purposes only. For actual IP protection strategies and definitive valuations, founders should consult with qualified patent attorneys who can provide professional legal advice tailored to their specific situation.
            <br /><br />
            <strong>Please note that all assessment results are preliminary and may not be confidential if not using a paid AI account with the proper safeguards so that AI will not train on the data. The app is provided free of charge and there is no attorney client relationship by using this app. Further, proper creation and capture of IP value require tailored legal and valuation strategies—founders should consult a qualified patent attorney to develop a robust IP portfolio and maximize startup valuation. Please visit PatentPC.com to consult with a patent lawyer.</strong>
          </div>

<Disclaimer />
        </div>
      </div>
      
    </>
  );
};

// Gap Analysis Results Component
const GapAnalysisResults = ({ selectedAssets, ownershipDocs, industry, generateGapAnalysis }) => {
  const { gaps, recommendations } = generateGapAnalysis();

  return (
    <div>
      <div className="ip-assessment-asset-card">
        <h3 style={{ color: '#e74c3c', marginBottom: '20px' }}>🔍 Identified Gaps</h3>
        {gaps.length > 0 ? (
          <ul style={{ color: '#e74c3c', marginLeft: '20px' }}>
            {gaps.map((gap, index) => (
              <li key={index} style={{ marginBottom: '8px' }}>{gap}</li>
            ))}
          </ul>
        ) : (
          <p style={{ color: '#27ae60' }}>✅ No critical gaps identified in current assessment scope</p>
        )}
      </div>

      <div className="ip-assessment-asset-card">
        <h3 style={{ color: '#2c3e50', marginBottom: '20px' }}>📋 Prioritized Recommendations</h3>
        {recommendations.length > 0 ? (
          recommendations.map((rec, index) => {
            const priorityColor = rec.priority === 'Critical' ? '#e74c3c' :
              rec.priority === 'High' ? '#f39c12' : '#3498db';
            return (
              <div key={index} style={{ borderLeft: `4px solid ${priorityColor}`, paddingLeft: '15px', marginBottom: '20px' }}>
                <h4 style={{ color: priorityColor, marginBottom: '8px' }}>{rec.priority} Priority</h4>
                <strong>Action:</strong> {rec.action}<br />
                <strong>Timeline:</strong> {rec.timeline}<br />
                <strong>Estimated Cost:</strong> {rec.estimatedCost}
              </div>
            );
          })
        ) : (
          <p style={{ color: '#27ae60', textAlign: 'center', fontSize: '1.1em' }}>
            🎉 Your IP portfolio shows good foundational coverage. Consider working with a patent attorney to optimize and expand your protections.
          </p>
        )}
      </div>
    </div>
  );
};

// Dashboard Results Component
const DashboardResults = ({ assessmentData, selectedAssets, ownershipDocs, formData }) => {
  const assetCount = selectedAssets?.length || 0;
  const ownershipScore = Math.min(100, (ownershipDocs?.length || 0) * 20);
  const protectionScore = assetCount > 0 ? Math.min(100, assetCount * 15) : 0;

  const generateInsights = () => {
    let insights = [];

    if (assessmentData.valuation && assessmentData.valuation.final > 100000) {
      insights.push('💰 Your IP portfolio represents significant value - consider IP insurance to protect these assets');
    }

    if (formData.industry === 'software' && selectedAssets?.some(asset => asset.id === 'softwareCode')) {
      insights.push('💻 For software companies, consider implementing source code escrow for enterprise customers');
    }

    if (formData.companyStage === 'growth' || formData.companyStage === 'early-revenue') {
      insights.push('🚀 Your company stage suggests it\'s time to expand international IP protection in key markets');
    }

    insights.push('📊 Regular IP audits (annually) help maintain and optimize your portfolio value');
    insights.push('🤝 Consider IP licensing opportunities to monetize unused assets');

    return insights;
  };

  return (
    <div>
      <div className="ip-assessment-summary-grid">
        <div className="ip-assessment-summary-card">
          <h4>Total IP Assets</h4>
          <div className="value">{assetCount}</div>
        </div>
        <div className="ip-assessment-summary-card">
          <h4>Ownership Score</h4>
          <div className="value">{ownershipScore}%</div>
        </div>
        <div className="ip-assessment-summary-card">
          <h4>Protection Level</h4>
          <div className="value">{protectionScore}%</div>
        </div>
        <div className="ip-assessment-summary-card">
          <h4>R&D Investment</h4>
          <div className="value">${(assessmentData.rdInvestment || 0).toLocaleString()}</div>
        </div>
      </div>

      {assessmentData.valuation && (
        <div className="ip-assessment-valuation-result">
          <h3>Estimated IP Portfolio Value</h3>
          <div className="amount">${assessmentData.valuation.final.toLocaleString()}</div>
          <p>Primary Methodology: Cost-Based Approach</p>
          <div style={{ marginTop: '20px', fontSize: '0.9em', opacity: '0.9' }}>
            <strong>Valuation Breakdown:</strong><br />
            Cost-Based: ${assessmentData.valuation.costBased.toLocaleString()}<br />
            Market-Based: ${assessmentData.valuation.marketBased.toLocaleString()}<br />
            {assessmentData.valuation.incomeBased > 0 && (
              <>Income-Based: ${assessmentData.valuation.incomeBased.toLocaleString()}<br /></>
            )}
            <em>{assessmentData.valuation.methodology}</em>
          </div>
        </div>
      )}

      <div className="ip-assessment-asset-card">
        <h3 style={{ marginBottom: '20px' }}>💡 Actionable Insights</h3>
        <ul style={{ marginLeft: '20px' }}>
          {generateInsights().map((insight, index) => (
            <li key={index} style={{ marginBottom: '12px', fontSize: '1.1em' }}>{insight}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default IPAssetAssessmentTool;